import type {ReactElement} from 'react';
import {PopupMenuItem} from '@Components/popup-menu/components';
import {openMessageModal} from '@Modals/message-modal';
import {CACHE_KEY_FOR_SCHEDULING, startSocialMediaPublishing} from '@Libraries/social-media-grid.library';
import {setCookie} from '@Utils/cookie.util';
import {GA4EventName} from '@Libraries/ga-events';
import {isEventsFeatureEnabled, openEventWizardWithStartDatePrefillFromContentPlanner} from '@Libraries/events.library';
import {useAppDispatch} from '@/hooks';
import {startEventCreationFlowForDate} from '@Components/content-planner/content-planner-reducer';
import {isTabletScreenWidth} from '@Utils/dom.util';
import {openCreateEventModal} from '@Modals/create-event-modal';
import {PopupMenuItemIconSize} from '@Components/popup-menu/components/popup-menu-item/popup-menu-item';

interface AddPostMenuItemsProps {
  date: Date;
}

enum EmailSelectionType {
  EMAIL = 'EMAIL',
  DESIGN_CAMPAIGN = 'DESIGN_CAMPAIGN',
  TEMPLATE = 'TEMPLATE',
}

const hoursToScheduleAfter = 1;
const minutesToScheduleAfter = 15;
const currentDate = new Date();

export const CONTENT_PLANNER_POST_COOKIE = 'pmwcontentplannerpost';

export function AddPostMenuItems({date}: AddPostMenuItemsProps): ReactElement<any> | null {
  const dispatch = useAppDispatch();
  const getSchedulableDate = (dateToAlter: Date): Date => {
    dateToAlter.setHours(currentDate.getHours() + hoursToScheduleAfter);
    dateToAlter.setMinutes(minutesToScheduleAfter);
    dateToAlter.setSeconds(0);
    return dateToAlter;
  };

  const startCreateEventFlow = (): void => {
    if (isEventsFeatureEnabled()) {
      openEventWizardWithStartDatePrefillFromContentPlanner(date);
      return;
    }

    dispatch(startEventCreationFlowForDate(date));
    window.PMW.gtm.trackGA4CustomEvent(GA4EventName.CONTENTPLANNER_ADD_CUSTOM_EVENT);

    if (isTabletScreenWidth()) {
      openCreateEventModal();
    }
  };

  const startPublishing = (): void => {
    setCookieForContentPlanner();
    window.PMW.gtm.trackGA4CustomEvent(GA4EventName.CONTENTPLANNER_ADD_SOCIAL_POST);
    window.PMW.setCacheItem(CACHE_KEY_FOR_SCHEDULING, getSchedulableDate(date), window.PMW.CACHE_EXPIRY_FIVE_MINUTES);
    startSocialMediaPublishing();
  };

  const setContentPlannerRedirectCache = (): void => {
    window.PMW.redirectUrlCache.setCache(
      window.PMW.redirectUrlCache.CONTENT_PLANNER_RETURN_CACHE_KEY,
      window.PMW.redirectUrlCache.CONTENT_PLANNER_RETURN_CACHE_VALUE,
      window.PMW.redirectUrlCache.CONTENT_PLANNER_DEFAULT_CACHE_TIMEOUT_MILLISECONDS
    );
  };

  const onClickCreateFromScratchBtn = (): void => {
    document.querySelector('.js-create-from-scratch')?.addEventListener('click', setContentPlannerRedirectCache);
  };

  const setCookieForContentPlanner = (): void => {
    setCookie(CONTENT_PLANNER_POST_COOKIE, '1', 180);
  };

  const onClickPrimaryBtn = (id: string, type: EmailSelectionType): void => {
    setContentPlannerRedirectCache();
    if (type !== EmailSelectionType.DESIGN_CAMPAIGN) {
      const schedulableDate = getSchedulableDate(date);
      window.PMW.setCacheItem(CACHE_KEY_FOR_SCHEDULING, schedulableDate, window.PMW.CACHE_EXPIRY_FIVE_MINUTES);
    }

    switch (type) {
      case EmailSelectionType.EMAIL:
        window.location.href = window.PMW.util.site_url(`emailmaker/load/${id}`);
        break;
      case EmailSelectionType.TEMPLATE:
        window.location.href = window.PMW.util.site_url(`emailmaker/copy/${id}`);
        break;
      case EmailSelectionType.DESIGN_CAMPAIGN:
        window.location.href = window.PMW.util.site_url(`emailmarketing/publish/campaign/${id}/scheduled`);
        break;
      default:
        openMessageModal({
          title: window.i18next.t('pmwjs_error'),
          text: window.i18next.t('pmwjs_error_processing_request_reload_try_again'),
        });
        break;
    }
  };

  const startEmailCampaign = (): void => {
    window.PMW.util.require(typeof window.PMW.openEmailSelector === 'undefined', 'emailSelector', true, () => {
      window.PMW.gtm.trackGA4CustomEvent(GA4EventName.CONTENTPLANNER_ADD_EMAIL);
      window.PMW.openEmailSelector(window.i18next.t('pmwjs_create_campaign_select_email'), onClickPrimaryBtn, onClickCreateFromScratchBtn);
    });
  };

  return (
    <>
      <PopupMenuItem
        text={window.i18next.t('pmwjs_event')}
        itemClasses="_borderless -smaller -less-margin-bottom"
        textClasses="body-lg-regular"
        iconClasses="icon icon-event content-heading _unmargin"
        clickHandler={startCreateEventFlow}
        horizontalAlignStart
        iconSize={PopupMenuItemIconSize.SMALL}
      />
      <PopupMenuItem
        text={window.i18next.t('pmwjs_email_campaign')}
        textClasses="body-lg-regular"
        itemClasses="_borderless -smaller -less-margin-bottom"
        iconClasses="icon icon-envelope content-heading _unmargin"
        horizontalAlignStart
        clickHandler={startEmailCampaign}
        iconSize={PopupMenuItemIconSize.SMALL}
      />
      <PopupMenuItem
        text={window.i18next.t('pmwjs_social_media_post')}
        textClasses="body-lg-regular"
        itemClasses="_borderless -smaller -less-margin-bottom"
        iconClasses="icon icon-social-media content-heading _unmargin"
        horizontalAlignStart
        clickHandler={startPublishing}
        iconSize={PopupMenuItemIconSize.SMALL}
      />
    </>
  );
}
